'use client';

import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Icon,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import type { Plan } from '@features/onboarding/plans';
import { PLANS } from '@features/onboarding/plans';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { FaCheck } from 'react-icons/fa';
import { useOnboardingStore } from 'stores/rootStore';

interface PlanCardProps {
  plan: Plan;
  isSelected?: boolean;
  onCtaClick?: (plan: Plan) => void;
}

const PlanCard = ({ plan, isSelected, onCtaClick }: PlanCardProps) => {
  const isHighlighted = plan.highlighted ?? false;

  const borderColor = isSelected
    ? 'blue.500'
    : isHighlighted
      ? 'transparent'
      : 'border.light';

  return (
    <Card.Root
      flex={1}
      minW={0}
      position="relative"
      bg={isHighlighted ? { base: 'gray.800', _dark: 'surface.inverse' } : 'surface.background'}
      borderColor={borderColor}
      borderWidth={isSelected ? 2 : 1}
      borderRadius="lg"
      p={6}
      boxShadow={isHighlighted ? 'lg' : undefined}
    >
      {plan.badge != null && (
        <Badge
          position="absolute"
          top={3}
          right={3}
          fontSize="xs"
          fontWeight="semibold"
          bg={{ base: 'gray.200', _dark: 'surface.accent' }}
          color={{ base: 'gray.800', _dark: 'text.primary' }}
          px={2}
          py={0.5}
          borderRadius="md"
        >
          {plan.badge}
        </Badge>
      )}
      <Card.Body>
        <Flex flexDirection="column" gap={4} height="full">
          <Box>
            <Heading
              size="lg"
              color={isHighlighted ? { base: 'white', _dark: 'text.inverse' } : 'text.primary'}
            >
              {plan.name}
            </Heading>
            <Text
              fontSize="lg"
              fontWeight="medium"
              color={
                isHighlighted ? { base: 'gray.300', _dark: 'text.secondary' } : 'text.secondary'
              }
            >
              {plan.price}
            </Text>
          </Box>
          <Text
            fontSize="sm"
            color={isHighlighted ? { base: 'gray.300', _dark: 'text.secondary' } : 'fg.muted'}
          >
            {plan.description}
          </Text>
          <Stack gap={3} flex={1}>
            {plan.features.map((feature) => (
              <Flex key={feature.text} alignItems="center" gap={2}>
                <Icon
                  as={FaCheck}
                  boxSize={4}
                  color={isHighlighted ? { base: 'white', _dark: 'text.inverse' } : 'text.primary'}
                  flexShrink={0}
                />
                <Text
                  fontSize="sm"
                  color={isHighlighted ? { base: 'white', _dark: 'text.inverse' } : 'text.primary'}
                >
                  {feature.text}
                </Text>
              </Flex>
            ))}
          </Stack>
          {plan.selectable === true ? (
            <Button
              w="full"
              size="md"
              variant={isSelected ? 'solid' : isHighlighted ? 'solid' : 'outline'}
              colorPalette={isSelected ? 'blue' : isHighlighted ? 'gray' : undefined}
              bg={
                isSelected
                  ? undefined
                  : isHighlighted
                    ? { base: 'white', _dark: 'surface.background' }
                    : undefined
              }
              color={
                isSelected
                  ? undefined
                  : isHighlighted
                    ? { base: 'gray.800', _dark: 'text.primary' }
                    : undefined
              }
              borderColor={isSelected || isHighlighted ? undefined : 'border.light'}
              onClick={onCtaClick != null ? () => onCtaClick(plan) : undefined}
            >
              {isSelected ? <><Icon as={FaCheck} boxSize={4} /> Selected</> : plan.ctaLabel}
            </Button>
          ) : (
            <Button w="full" size="md" variant="outline" borderColor="border.light" asChild>
              <a href="mailto:sales@nullifyrisk.com">{plan.ctaLabel}</a>
            </Button>
          )}
        </Flex>
      </Card.Body>
    </Card.Root>
  );
};

interface Props {
  getNextStepPath?: () => string | undefined;
}

const ChoosePlanPage = ({ getNextStepPath }: Props) => {
  const router = useRouter();
  const onboardingStore = useOnboardingStore();

  const onSelectPlan = useCallback(
    (plan: Plan) => {
      onboardingStore.setSelectedPlanKey(plan.key);
    },
    [onboardingStore],
  );

  const onContinue = useCallback(() => {
    const nextStepPath = getNextStepPath?.();
    if (nextStepPath != null) {
      router.push(nextStepPath);
    }
  }, [getNextStepPath, router]);

  return (
    <Flex flex={1} minH={0} flexDirection="column" justifyContent="center">
      <Flex
        flexDirection="column"
        alignItems="center"
        gap={8}
        px={{ base: 4, md: 8, lg: 12 }}
        py={{ base: 8, md: 12 }}
        maxW="1400px"
        mx="auto"
        w="full"
      >
        <Flex flexDirection="column" gap={2} textAlign="center" maxW="600px">
          <Heading
            as="h1"
            fontSize={{ base: '3xl', md: '4xl' }}
            fontWeight="bold"
            color={{ base: 'gray.900', _dark: 'white' }}
          >
            Choose your plan
          </Heading>
          <Text color={{ base: 'gray.600', _dark: 'gray.400' }}>
            Select the plan that best fits your needs
          </Text>
        </Flex>
        <SimpleGrid w="full" gap={6} columns={{ base: 1, md: 2, lg: 4 }}>
          {PLANS.map((plan) => (
            <PlanCard
              key={plan.key}
              plan={plan}
              isSelected={plan.key === onboardingStore.selectedPlanKey}
              onCtaClick={onSelectPlan}
            />
          ))}
        </SimpleGrid>
        {onboardingStore.hasSelectedPlan && (
          <Button size="lg" onClick={onContinue}>
            Continue
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export default observer(ChoosePlanPage);
